import React, { useEffect, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import { useFetchSystemsQuery, useRequestLogsMutation } from '~/api/logs/LoggingService.api';
import { IncomingLogsRequestDto } from '~/api/models/IncomingLogsRequestDto';
import { CM4 } from '~/features/devices/constants/deviceTypes';
import Notification from '@rio-cloud/rio-uikit/Notification';
import DateRangePickerOTA from '~/features/base/components/DateRangePickerOTA/DateRangePickerOTA';
import MultiSelectOTA from '~/features/base/components/MultiSelectOTA';
import moment from 'moment/moment';
import CM4RequestLogButton from '~/features/deviceLogs/components/logRequest/CM4RequestLogButton';
import SerialNumbersFormItem from '~/features/base/components/forms/SerialNumbersFormItem';
import { validateHwSerials } from '~/features/base/utils/hwSerialValidator';
import { DateRangePreset } from '~/features/base/utils/customPresets';
import EulaDeclinedConfirmation from '~/features/deviceLogs/components/EulaDeclinedConfirmation';
import { EULA_STATUS } from '~/features/devices/constants/eulaStatus';
import {
    MAX_DEVICES
} from '~/features/devices/components/dialogs/DownloadLogRequestBulk/DownloadLogRequestsInBulkDialog';

const ETHERNET_TRACE = 'ETHERNET_TRACE';

interface CM4LogRequestProps {
    serialNumberList: string[];
    isDeviceLogsUser: boolean;
    requestDisabled: boolean;
    showDeviceListForm?: boolean;
    hideRequestButton?: boolean;
    customPresets?: DateRangePreset[];
    maxDaysOfLogs?: number;
    onFormSubmit?: () => {};
    eulaStatus?: string;
    testReleasesActive?: boolean;
}

export interface CM4LogRequestForm {
    requestedSystem: string[];
    serialNumberList: string[];
    dateInput: { startValue: moment.Moment, endValue: moment.Moment };
}

const CM4LogRequest: React.FC<CM4LogRequestProps> = ({
    serialNumberList,
    isDeviceLogsUser,
    requestDisabled,
    showDeviceListForm,
    hideRequestButton, customPresets,
    maxDaysOfLogs,
    onFormSubmit,
    eulaStatus,
    testReleasesActive
}) => {
    const { data: systems } = useFetchSystemsQuery(CM4);
    const [requestLogs] = useRequestLogsMutation();
    const [showEulaDialog, setShowEulaDialog] = useState(false);
    const [pendingData, setPendingData] = useState<any>(null);

    const todayTimeRange = {
        startValue: moment().startOf('day'),
        endValue: moment(),
    };
    const {
        handleSubmit,
        formState: { errors },
        control,
        reset,
        setValue,
    } = useFormContext();

    const handleLogRequest = async(requestParams: IncomingLogsRequestDto) => {
        await requestLogs(requestParams)
            .unwrap()
            .then(() => {
                Notification.success(<FormattedMessage
                    id='intl-msg:logs:logRequestWithSuccess'/>);
                reset({
                    dateInput: { startValue: todayTimeRange.startValue, endValue: todayTimeRange.endValue },
                    requestedSystem: systems?.map(system => system.id)
                });
                onFormSubmit && onFormSubmit();

            })
            .catch((error) => {
                Notification.error(<FormattedMessage id='intl-msg:logs:logRequestWithoutSuccess' values={
                    { error: error?.data?.message }
                }/>);
            })
    };

    const onSubmit = async data => {
        const shouldShowDialog = eulaStatus === EULA_STATUS.DECLINED && testReleasesActive;

        if (shouldShowDialog) {
            setPendingData(data);
            setShowEulaDialog(true);
            return;
        }

        await submitRequest(data);
    };

    const submitRequest = async (data) => {
        const requestParams: IncomingLogsRequestDto = {
            deviceType: CM4,
            serialNumbersList: data.serialNumberList || serialNumberList,
            systems: data.requestedSystem,
            period: {
                minValue: data.dateInput.startValue,
                maxValue: data.dateInput.endValue
            }

        };
        await handleLogRequest(requestParams);
    };

    const handleEulaConfirm = async () => {
        setShowEulaDialog(false);
        if (pendingData) {
            await submitRequest(pendingData);
            setPendingData(null);
        }
    };


    useEffect(() => {
        if (systems) {
            setValue('requestedSystem', systems.filter(system => system.id !== ETHERNET_TRACE).map(system => system.id));
        }
    }, [systems]);


    return (
        <>
            <EulaDeclinedConfirmation
                show={showEulaDialog}
                onHide={() => setShowEulaDialog(false)}
                onConfirm={handleEulaConfirm}
            />
            <form id={'cm4-log-form'} onSubmit={handleSubmit(onSubmit)}>
            {showDeviceListForm && <Controller
                control={control}
                name='serialNumberList'
                defaultValue={[]}
                rules={{
                    validate: {
                        maxLength: (serialNumbers: string[]) =>
                            serialNumbers.length <= MAX_DEVICES || `Maximum ${MAX_DEVICES} devices allowed`,
                        validSerials: (serialNumbers: string[]) =>
                            validateHwSerials(serialNumbers)
                    },
                    required: 'This field is required',
                }}
                render={({ field: { onChange, value }, fieldState: { error } }) => (
                    <>
                        <SerialNumbersFormItem
                            label={'intl-msg:serialNumberList'}
                            serialNumbers={value}
                            disabled={false}
                            onChange={({ serialNumbers }) => {
                                onChange(serialNumbers);
                            }}
                        />
                        {error?.message && (

                            <div className={'form-group has-feedback has-error  padding-bottom-15'}>
                    <span className='help-block'>
                        <span>                                 {error.message}
                            </span>
                        </span>
                            </div>

                        )}
                    </>
                )}
            />}
                <label className='control-label'>
                    <FormattedMessage id='intl-msg:logs:systemLogging'/>
                </label>
                <Controller
                    control={control}
                    name='requestedSystem'
                    rules={{
                        required: true,
                        validate: {
                            ethernetTraceValidation: (value: string[]) => {
                                const hasEthernetTrace = value?.includes(ETHERNET_TRACE);
                                if (hasEthernetTrace && value.length > 1) {
                                    return 'intl-msg:ethernetTraceCanOnlyBeSelectedAlone';
                                }
                                return true;
                            }
                        }
                    }}
                    render={({ field: { value, onChange }, }) => {
                        const handleChange = (newValue: string[]) => {
                            const hasEthernetTrace = newValue?.includes(ETHERNET_TRACE);
                            const previousHasEthernetTrace = value?.includes(ETHERNET_TRACE);

                            if (hasEthernetTrace && !previousHasEthernetTrace) {
                                onChange([ETHERNET_TRACE]);
                            } else if (hasEthernetTrace && newValue.length > 1) {
                                onChange(newValue.filter(item => item !== ETHERNET_TRACE));
                            } else {
                                onChange(newValue);
                            }
                        };

                        return (<MultiSelectOTA
                            hasError={!!errors.requestedSystem}
                            placeholder={<FormattedMessage id='intl-msg:logs:loggingSystemSelectionPlaceholder'/>}
                            options={systems?.map(option => ({ ...option }))}
                            value={value}
                            onChange={handleChange}
                        />);
                    }
                    }
                />

                <label className='margin-top-20 control-label'>
                    <FormattedMessage id='intl-msg:overWriteDate'/>
                </label>
                <div className={'display-flex flex-row justify-content-between'}>
                    <Controller
                        control={control}
                        name='dateInput'
                        rules={{
                            required: true,
                            validate: {
                                isValidDateRange: (value) => {
                                    const minDate = moment('1970-01-01');
                                    if (moment(value.startValue).isBefore(minDate)) {
                                        return 'intl-msg:form:minDateError';
                                    }
                                    if (maxDaysOfLogs && moment(value.endValue).diff(moment(value.startValue), 'days') > maxDaysOfLogs) {
                                        return 'intl-msg:max10DateRangeExceeded';
                                    }
                                    return true;
                                },
                            }
                        }}
                        render={({ field }) => <DateRangePickerOTA
                            customPresets={customPresets}
                            hasError={!!errors.dateInput}
                            errorMsg={errors?.dateInput?.message as string}
                            initialStartValue={field.value?.startValue}
                            initialEndValue={field.value?.endValue}
                            onRangeChange={(startValue, endValue) => {
                                field.onChange({ startValue, endValue });

                            }}/>}/>
                    <>
                        {!hideRequestButton && <CM4RequestLogButton
                            disabled={requestDisabled}
                            isUserAllowed={isDeviceLogsUser}
                        />}

                    </>
                </div>

            </form>
        </>
    );
};

export default CM4LogRequest;
